import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Star, CheckCircle2, XCircle, Award, Eye, EyeOff, Trash2 } from "lucide-react";

export default function AdminTestimonials() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all"); // all | pending | approved

  const load = () => api.get("/admin/testimonials").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const update = async (t, fields) => {
    try {
      await api.patch(`/admin/testimonials/${t.testimonial_id}`, fields);
      load();
    } catch {
      toast.error("Update failed");
    }
  };
  const remove = async (t) => {
    if (!confirm("Delete this testimonial? This cannot be undone.")) return;
    await api.delete(`/admin/testimonials/${t.testimonial_id}`);
    toast.success("Testimonial deleted");
    load();
  };

  const filtered = items.filter((t) => {
    if (filter === "approved") return t.is_approved;
    if (filter === "pending") return !t.is_approved;
    return true;
  });

  return (
    <div data-testid="admin-testimonials">
      <div className="flex justify-between items-start mb-8">
        <div>
          <p className="overline">Reviews</p>
          <h1 className="text-3xl font-heading font-medium text-[#0A2540] mt-2">Client Testimonials</h1>
          <p className="text-sm text-[#475569] mt-2 max-w-xl">
            Approve testimonials to feature them in the login-page carousel. Only testimonials
            where the client opted in to share publicly are shown.
          </p>
        </div>
        <div className="flex gap-2 bg-white border border-[#E2E8F0] rounded-md p-1">
          {[
            { k: "all", l: "All" },
            { k: "pending", l: "Pending" },
            { k: "approved", l: "Live" },
          ].map((f) => (
            <button
              key={f.k}
              onClick={() => setFilter(f.k)}
              data-testid={`filter-${f.k}`}
              className={`text-xs px-3 py-1.5 rounded ${filter === f.k ? "bg-[#0A2540] text-white" : "text-[#475569] hover:text-[#0A2540]"}`}
            >
              {f.l}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        {filtered.map((t) => (
          <div key={t.testimonial_id} className="card-pb p-5" data-testid={`tst-${t.testimonial_id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-2">
                  <div className="font-medium text-[#0A2540]">{t.user_name}</div>
                  <span className="text-xs text-[#475569] font-mono">{t.customer_code}</span>
                  <div className="flex gap-0.5" data-testid={`tst-rating-${t.testimonial_id}`}>
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star
                        key={s}
                        className={`w-4 h-4 ${s <= t.rating ? "text-[#C9A356] fill-[#C9A356]" : "text-slate-300"}`}
                      />
                    ))}
                  </div>
                  {t.is_featured && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                      <Award className="w-3 h-3" /> Featured
                    </span>
                  )}
                  {t.is_approved ? (
                    <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Live</span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">Pending</span>
                  )}
                  {!t.allow_publish && (
                    <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700">Private</span>
                  )}
                </div>
                <p className="text-[#475569] text-sm italic">"{t.text}"</p>
                <div className="text-xs text-[#94A3B8] mt-2">
                  {t.project_title} · {new Date(t.created_at).toLocaleDateString()}
                </div>
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                {!t.is_approved ? (
                  <button
                    onClick={() => update(t, { is_approved: true })}
                    disabled={!t.allow_publish}
                    title={!t.allow_publish ? "Client did not opt in to share publicly" : "Approve for public display"}
                    data-testid={`approve-${t.testimonial_id}`}
                    className="btn-accent text-xs py-1.5 px-3 disabled:opacity-50"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                  </button>
                ) : (
                  <button
                    onClick={() => update(t, { is_approved: false })}
                    data-testid={`unapprove-${t.testimonial_id}`}
                    className="btn-outline-navy text-xs py-1.5 px-3"
                  >
                    <EyeOff className="w-3.5 h-3.5 mr-1" /> Hide
                  </button>
                )}
                {t.is_approved && (
                  <button
                    onClick={() => update(t, { is_featured: !t.is_featured })}
                    data-testid={`feature-${t.testimonial_id}`}
                    className="btn-outline-navy text-xs py-1.5 px-3"
                  >
                    <Award className="w-3.5 h-3.5 mr-1" /> {t.is_featured ? "Unfeature" : "Feature"}
                  </button>
                )}
                <button
                  onClick={() => remove(t)}
                  data-testid={`delete-${t.testimonial_id}`}
                  className="text-xs text-red-600 hover:text-red-700 inline-flex items-center justify-center py-1.5 px-3"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                </button>
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="card-pb p-12 text-center text-[#475569]">
            <Star className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            No testimonials in this view yet.
          </div>
        )}
      </div>
    </div>
  );
}
